import { Sparkles } from 'lucide-react'
import type { PointApiResponse } from '../services/pointActivityService'

export interface DailyReviewProgress {
  fraction: number
  percent: number
  pending: boolean
  label: string
}

export function getDailyReviewProgress(
  status?: PointApiResponse | null,
  activeSecondsOverride?: number | null
): DailyReviewProgress {
  if (!status) {
    return {
      fraction: 0,
      percent: 0,
      pending: true,
      label: 'Loading Daily Review progress…',
    }
  }

  const review = status.dailyReview
  const displayedActiveSeconds =
    typeof activeSecondsOverride === 'number'
      ? Math.max(
          review.activeSeconds,
          Math.min(activeSecondsOverride, review.requiredActiveSeconds)
        )
      : review.activeSeconds

  if (review.rewardedToday) {
    return {
      fraction: 1,
      percent: 100,
      pending: false,
      label: 'Daily Review earned today',
    }
  }
  if (review.ineligibleReason === 'viewer') {
    return {
      fraction: 0,
      percent: 0,
      pending: false,
      label: 'Viewers do not earn Daily Review Points',
    }
  }
  if (review.ineligibleReason === 'points_disabled') {
    return {
      fraction: 0,
      percent: 0,
      pending: false,
      label: 'Point rewards are currently disabled',
    }
  }
  if (review.ineligibleReason === 'daily_review_disabled') {
    return {
      fraction: 0,
      percent: 0,
      pending: false,
      label: 'Daily Review is currently disabled',
    }
  }
  if (review.rewardedDaysThisCycle >= review.maxRewardsPerCycle) {
    return {
      fraction: 0,
      percent: 0,
      pending: false,
      label: 'Daily Review reward limit reached for this Point cycle',
    }
  }
  if (
    status.wallet.pointsTotal >=
    status.wallet.maxPointsBalance
  ) {
    return {
      fraction: 0,
      percent: 0,
      pending: false,
      label: 'Point balance cap reached',
    }
  }

  const requirementFractions: number[] = []
  if (review.requiredActiveSeconds > 0) {
    requirementFractions.push(
      Math.min(1, displayedActiveSeconds / review.requiredActiveSeconds)
    )
  }
  if (review.requiredMeaningfulActions > 0) {
    requirementFractions.push(
      Math.min(1, review.meaningfulActions / review.requiredMeaningfulActions)
    )
  }

  const fraction = requirementFractions.length > 0
    ? Math.min(...requirementFractions)
    : 1
  const percent = Math.round(fraction * 100)

  return {
    fraction,
    percent,
    pending: true,
    label: `Daily Review ${percent}% · ${displayedActiveSeconds}/${review.requiredActiveSeconds}s · ${review.meaningfulActions}/${review.requiredMeaningfulActions} actions · +${review.rewardPoints} Points`,
  }
}

interface PointProgressRingProps {
  status?: PointApiResponse | null
  activeSecondsOverride?: number | null
  size?: number
}

export function PointProgressRing({
  status,
  activeSecondsOverride,
  size = 18,
}: PointProgressRingProps) {
  const progress = getDailyReviewProgress(status, activeSecondsOverride)

  if (!progress.pending) {
    return <Sparkles size={size} aria-hidden="true" />
  }

  const strokeWidth = 2
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference * (1 - progress.fraction)

  return (
    <span
      title={progress.label}
      aria-label={progress.label}
      role="img"
      className="relative inline-flex shrink-0"
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden="true"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.25"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 500ms ease' }}
        />
      </svg>
      <Sparkles
        size={Math.max(10, size - 7)}
        className="absolute inset-0 m-auto"
        aria-hidden="true"
      />
    </span>
  )
}
